import React, { memo, useMemo, useRef, useEffect } from 'react';
import { Box, Paper, useTheme } from '@mui/material';
import { TimeSlotColumn } from './TimeSlotColumn';
import { DayColumn } from './DayColumn';
import { ScheduleItem } from '../types/scheduleItem.types';
import { TimeScale, CalendarViewType } from '../types/calendar.types';
import { getDateRangeForView, getPixelsPerHour } from '../utils/timeUtils';

interface CalendarGridProps {
  viewType: CalendarViewType;
  dayViewDays?: number;
  currentDate: Date;
  items: ScheduleItem[];
  timeScale: TimeScale;
  onItemClick?: (item: ScheduleItem) => void;
  onItemDragStart?: (e: React.DragEvent, item: ScheduleItem) => void;
  onItemDragEnd?: () => void;
  onDrop?: (e: React.DragEvent, date: Date, time: Date) => void;
  onCreateClick?: (date: Date, time: Date) => void;
  allowDrag?: boolean;
  onOpenWorkOrder?: (invoiceDocId: number) => void;
}

const HEADER_HEIGHT = 50;
const HOURS_IN_DAY = 24;

export const CalendarGrid: React.FC<CalendarGridProps> = memo(({
  viewType,
  dayViewDays = 1,
  currentDate,
  items,
  timeScale,
  onItemClick,
  onItemDragStart,
  onItemDragEnd,
  onDrop,
  onCreateClick,
  allowDrag = true,
  onOpenWorkOrder,
}) => {
  const theme = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  const { dates } = getDateRangeForView(currentDate, viewType, dayViewDays);
  
  const isMonthView = viewType === 'month';

  // Calculate total grid height (24 hours * pixels per hour)
  const pixelsPerHour = useMemo(() => getPixelsPerHour(timeScale), [timeScale]);
  const gridHeight = useMemo(() => HOURS_IN_DAY * pixelsPerHour, [pixelsPerHour]);

  // Scroll to current time on mount and when view/timeScale changes
  useEffect(() => {
    if (isMonthView || !scrollContainerRef.current) return;

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    
    // Calculate scroll position: hours * pixelsPerHour + fraction of hour
    // Subtract some offset to show a bit before current time (1 hour buffer)
    const scrollPosition = Math.max(0, (currentHour - 1) * pixelsPerHour + (currentMinutes / 60) * pixelsPerHour);
    
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      scrollContainerRef.current?.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      });
    }, 100);
  }, [isMonthView, pixelsPerHour, viewType, currentDate]);

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        maxHeight:'93%',
      }}
    >
      {/* Scrollable container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          bgcolor: 'background.default',
        }}
      >
        {/* Sticky header row */}
        <Box
          sx={{
            display: 'flex',
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper',
          }}
        >
          {/* Time column header spacer */}
          {!isMonthView && (
            <Box
              sx={{
                width: 80,
                height: HEADER_HEIGHT,
                flexShrink: 0,
                borderRight: 1,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            />
          )}
          
          {/* Day headers */}
          {dates.map((date, index) => (
            <DayColumnHeader
              key={date.toISOString()}
              date={date}
              isLastColumn={index === dates.length - 1}
            />
          ))}
        </Box>

        {/* Content area with time slots and day columns */}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            minHeight: gridHeight,
          }}
        >
          {/* Time slot column (hidden in month view) */}
          {!isMonthView && <TimeSlotColumn timeScale={timeScale} showHeader={false} />}

          {/* Day columns container */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              minWidth: 0,
            }}
          >
            {dates.map((date, index) => (
              <DayColumn
                key={date.toISOString()}
                date={date}
                items={items}
                timeScale={timeScale}
                isFirstColumn={index === 0}
                isLastColumn={index === dates.length - 1}
                onItemClick={onItemClick}
                onItemDragStart={onItemDragStart}
                onItemDragEnd={onItemDragEnd}
                onDrop={onDrop}
                onCreateClick={onCreateClick}
                allowDrag={allowDrag}
                onOpenWorkOrder={onOpenWorkOrder}
                showHeader={false}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
});

CalendarGrid.displayName = 'CalendarGrid';

// Separate header component for sticky behavior
const DayColumnHeader: React.FC<{ date: Date; isLastColumn: boolean }> = memo(({ date, isLastColumn }) => {
  const today = useMemo(() => {
    const now = new Date();
    return date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();
  }, [date]);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 120,
        height: HEADER_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderRight: isLastColumn ? 'none' : 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: today ? 'primary.light' : 'background.paper',
      }}
    >
      <Box
        component="span"
        sx={{
          fontWeight: 600,
          color: today ? 'primary.contrastText' : 'text.secondary',
          textTransform: 'uppercase',
          fontSize: '0.75rem',
        }}
      >
        {date.toLocaleDateString('en-US', { weekday: 'short' })}
      </Box>
      <Box
        component="span"
        sx={{
          fontWeight: today ? 700 : 400,
          color: today ? 'primary.contrastText' : 'text.primary',
          fontSize: '0.875rem',
        }}
      >
        {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
      </Box>
    </Box>
  );
});

DayColumnHeader.displayName = 'DayColumnHeader';
